import { Link } from 'react-router-dom'

import type { Recommendation, RecommendationConfidence } from '@/api/types'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Badge, Card, EmptyState, Icon, LoadingBlock, type BadgeTone } from '@/design'
import { CATEGORY_LABELS } from '@/lib/insights/categoryLabels'

import { useDrillDown, useInsightAction, useInsightsQuery } from './hooks'
import './insights.css'

const CONFIDENCE_TONE: Record<RecommendationConfidence, BadgeTone> = {
  high: 'positive',
  medium: 'accent',
  low: 'neutral',
}

function RecommendationCard({ recommendation }: { recommendation: Recommendation }) {
  const href = useDrillDown()
  const dismiss = useInsightAction((client, id) => client.dismissRecommendation(id))

  return (
    <Card
      className="in-recommendation"
      title={recommendation.title}
      subtitle={
        <span className="in-recommendation__meta">
          <Badge>{CATEGORY_LABELS[recommendation.category]}</Badge>
          <Badge tone={CONFIDENCE_TONE[recommendation.confidence]}>{recommendation.confidence} confidence</Badge>
          {recommendation.entity ? <span className="in-muted">{recommendation.entity.label}</span> : null}
        </span>
      }
    >
      <p className="in-recommendation__explanation">{recommendation.explanation}</p>
      <dl className="in-metrics">
        {recommendation.metrics.map((metric) => (
          <div key={metric.label}>
            <dt>{metric.label}</dt>
            <dd>{metric.value}</dd>
          </div>
        ))}
      </dl>
      <p className="in-recommendation__impact">
        <strong>Estimated impact</strong> {recommendation.estimatedImpact}
      </p>
      <div className="in-action-row">
        <Link className="ci-button" data-variant="primary" to={href(recommendation.action.drillDown)}>
          {recommendation.action.label} <Icon name="arrowRight" size={14} />
        </Link>
        <button
          type="button"
          className="ci-button"
          data-variant="ghost"
          disabled={dismiss.isPending}
          onClick={() => dismiss.mutate(recommendation.id)}
        >
          Dismiss
        </button>
      </div>
    </Card>
  )
}

/**
 * Insights > Recommendations: suggested actions with the metrics behind them,
 * for a person to approve. Nothing here executes on its own — the action
 * button always opens the page where the change is actually made.
 */
export function RecommendationsPage() {
  const notLive = useNotLivePage('recommendations')
  const recommendations = useInsightsQuery('recommendations', (client, filters) => client.recommendations(filters))
  const items = recommendations.data?.data ?? []

  if (notLive) return <NotLivePage />

  if (recommendations.isPending) return <LoadingBlock rows={6} label="Loading recommendations" />
  if (recommendations.isError) {
    return (
      <Card>
        <EmptyState variant="outage" description="Recommendations could not be loaded." />
      </Card>
    )
  }
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState variant="no-results" title="No open recommendations" description="Nothing to review right now. New suggestions appear here as the figures change." />
      </Card>
    )
  }

  return (
    <div className="in-cards" aria-label="Recommendations">
      {items.map((recommendation) => (
        <RecommendationCard key={recommendation.id} recommendation={recommendation} />
      ))}
    </div>
  )
}
